import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutTemplate, Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectAnnouncements,
  selectAnnouncementsListStatus,
  selectAnnouncementsPage,
} from "../../features/announcements/announcements-slice";
import { fetchAnnouncements } from "../../features/announcements/slice-extensions/announcements-slice-extension";
import AnnouncementComposeDialog from "./AnnouncementComposeDialog";
import ManageTemplatesDialog from "./ManageTemplatesDialog";

/** Send history + the entry point for composing a new announcement. */
const AnnouncementsSection = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const announcements = useAppSelector(selectAnnouncements);
  const listStatus = useAppSelector(selectAnnouncementsListStatus);
  const { totalCount } = useAppSelector(selectAnnouncementsPage);
  const [composeOpen, setComposeOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAnnouncements({}));
  }, [dispatch]);

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(iso),
    );

  const audienceLabel = (audience: string, sessionTitle: string | null) =>
    audience === "Session"
      ? t("announcements.audienceSession", "Session: {{title}}", { title: sessionTitle ?? "—" })
      : t("announcements.audienceAll", "All members");

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t(
            "announcements.hint",
            "Email your members — term dates, price changes, anything they should hear from you.",
          )}
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setTemplatesOpen(true)}>
            <LayoutTemplate size={16} />
            {t("announcements.manageTemplates", "Templates")}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setComposeOpen(true)}>
            <Plus size={16} />
            {t("announcements.new", "New announcement")}
          </button>
        </div>
      </div>

      {listStatus === "loading" && announcements.length === 0 && (
        <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>
      )}

      {listStatus !== "loading" && announcements.length === 0 && (
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {t("announcements.empty", "Nothing sent yet. Your sent announcements will appear here.")}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {announcements.map((announcement) => (
          <li
            key={announcement.id}
            className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-grey-20 dark:text-white">{announcement.subject}</p>
                <p className="text-sm text-grey-40 dark:text-grey-80">
                  {audienceLabel(announcement.audience, announcement.sessionTitle)} ·{" "}
                  {formatDate(announcement.dateCreated)}
                </p>
              </div>
              <span className="rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-40 dark:bg-primary-20 dark:text-grey-80">
                {t("announcements.sentTo", "Sent to {{count}}", { count: announcement.recipientCount })}
                {announcement.skippedCount > 0 &&
                  ` · ${t("announcements.skipped", "{{count}} skipped", { count: announcement.skippedCount })}`}
              </span>
            </div>
            <p className="mt-2 line-clamp-3 whitespace-pre-line text-sm text-grey-20 dark:text-white">
              {announcement.body}
            </p>
          </li>
        ))}
      </ul>

      {totalCount > announcements.length && (
        <p className="mt-3 text-xs text-grey-40 dark:text-grey-80">
          {t("announcements.showingLatest", "Showing the latest {{shown}} of {{total}}", {
            shown: announcements.length,
            total: totalCount,
          })}
        </p>
      )}

      {composeOpen && <AnnouncementComposeDialog onClose={() => setComposeOpen(false)} />}
      {templatesOpen && <ManageTemplatesDialog onClose={() => setTemplatesOpen(false)} />}
    </div>
  );
};

export default AnnouncementsSection;
